import { Box, Typography } from '@mui/material';
import { CheckCircle } from '@phosphor-icons/react';

interface Step {
  label: string;
  emoji: string;
}

interface WizardStepperProps {
  currentStep: number;
  completedSteps: Set<number>;
  steps: Step[];
  /**
   * When true, upcoming steps are visually dimmed (grayscale + opacity).
   * Default: true (keeps existing brand/store wizard behavior).
   */
  dimUpcoming?: boolean;
}

const WizardStepper = ({
  currentStep,
  completedSteps,
  steps,
  dimUpcoming = true,
}: WizardStepperProps) => {
  const getStepState = (
    index: number
  ): 'completed' | 'current' | 'upcoming' => {
    if (completedSteps.has(index)) return 'completed';
    if (index === currentStep) return 'current';
    return 'upcoming';
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'lightGray.main',
        py: 3,
        px: 4,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: '800px',
          margin: '0 auto',
        }}
      >
        {steps.map((step, index) => {
          const state = getStepState(index);
          const isCompleted = state === 'completed';
          const isCurrent = state === 'current';
          const isUpcoming = state === 'upcoming';
          const isLast = index === steps.length - 1;

          return (
            <Box
              key={`${step.label}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {/* Step indicator */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                {/* Circle with icon/checkmark */}
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: isCompleted
                      ? 'success.main'
                      : isCurrent
                        ? 'primary.main'
                        : 'lightGray.main',
                    transition: 'all 0.3s ease',
                    animation: isCurrent
                      ? 'pulse 2s ease-in-out infinite'
                      : 'none',
                    '@keyframes pulse': {
                      '0%, 100%': {
                        transform: 'scale(1)',
                        opacity: 1,
                      },
                      '50%': {
                        transform: 'scale(1.05)',
                        opacity: 0.9,
                      },
                    },
                  }}
                >
                  {isCompleted ? (
                    <CheckCircle size={32} weight="fill" color="white" />
                  ) : (
                    <Typography
                      sx={{
                        fontSize: '24px',
                        filter:
                          dimUpcoming && isUpcoming
                            ? 'grayscale(100%)'
                            : 'none',
                        opacity: dimUpcoming && isUpcoming ? 0.5 : 1,
                      }}
                    >
                      {step.emoji}
                    </Typography>
                  )}
                </Box>

                {/* Step label */}
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: isCurrent ? 600 : 400,
                    color: isCompleted
                      ? 'success.main'
                      : isCurrent
                        ? 'primary.main'
                        : 'lightGray.text',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {step.label}
                </Typography>
              </Box>

              {/* Connecting line */}
              {!isLast && (
                <Box
                  sx={{
                    width: '120px',
                    height: '2px',
                    mx: 2,
                    bgcolor: isCompleted ? 'success.main' : 'lightGray.main',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'background-color 0.3s ease',
                  }}
                >
                  {/* Animated line for current step connection */}
                  {isCurrent && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: '-100%',
                        width: '100%',
                        height: '100%',
                        bgcolor: 'primary.main',
                        animation: 'slideIn 1s ease-in-out forwards',
                        '@keyframes slideIn': {
                          '0%': {
                            left: '-100%',
                          },
                          '100%': {
                            left: '0%',
                          },
                        },
                      }}
                    />
                  )}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default WizardStepper;
